import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Toolbar,
  Typography,
  makeStyles,
} from '@material-ui/core';
import * as React from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { DelayedLinearProgress, SnackbarContext } from 'components';
import { FiscalOutlet, FiscalReading } from 'interfaces';
import { FiscalService } from 'services';

/**
 * X and Z readings, one card per shop.
 *
 * X is read-only and can be taken as often as anyone asks, which is what the
 * tax department wants on a spot check. Z closes the period and CANNOT BE
 * UNDONE, so it sits behind a confirmation that says exactly what it will do.
 */
const useStyles = makeStyles((theme) => ({
  card: { marginBottom: theme.spacing(2) },
  meta: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
  actions: { marginTop: theme.spacing(1) },
  xButton: { marginRight: theme.spacing(2) },
  slip: {
    fontFamily: 'monospace',
    whiteSpace: 'pre',
    fontSize: '0.8rem',
    lineHeight: 1.35,
    overflowX: 'auto',
    padding: theme.spacing(2),
    background: '#fafafa',
    border: '1px solid #e0e0e0',
  },
  warn: { color: theme.palette.error.main },
}));

export const TillReadings: React.FC = () => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);

  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [outlets, setOutlets] = React.useState<FiscalOutlet[]>([]);
  const [print, setPrint] = React.useState(true);
  const [reading, setReading] = React.useState<FiscalReading | null>(null);
  const [confirmZ, setConfirmZ] = React.useState<FiscalOutlet | null>(null);

  const load = React.useCallback(() => {
    setLoading(true);
    FiscalService.getOutlets()
      .then((r) => {
        setOutlets(r);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  React.useEffect(load, [load]);

  const take = (outlet: FiscalOutlet, type: 'x' | 'z') => {
    setBusy(true);
    setReading(null);
    FiscalService.takeReading(outlet.id, type, print && outlet.hasPrinter)
      .then((r) => {
        setReading(r);
        setBusy(false);
        if (r.printError) {
          snackbar.error(`Reading taken, but it did not print: ${r.printError}`);
        } else if (r.printJobId) {
          snackbar.success(`${r.type} reading sent to the printer.`);
        }
        load();
      })
      .catch(() => {
        setBusy(false);
        snackbar.error('Could not take the reading.');
      });
  };

  return (
    <React.Fragment>
      <BreadcrumbsItem to="/till-readings">Till Readings</BreadcrumbsItem>
      <DelayedLinearProgress loading={loading || busy} />

      <Toolbar style={{ justifyContent: 'space-between', paddingLeft: 0 }}>
        <Typography variant="h6">Till Readings</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={print}
              onChange={(e) => setPrint(e.target.checked)}
              color="primary"
            />
          }
          label="Print it"
        />
      </Toolbar>

      {outlets.map((o) => (
        <Card key={o.id} className={classes.card}>
          <CardContent>
            <Typography variant="h6">{o.name}</Typography>
            <Typography variant="body2" className={classes.meta}>
              Till {o.tillCode ?? 'not set'}
              {' · '}
              {o.lastZ === 0 ? 'no Z taken yet' : `last reading was Z ${o.lastZ}`}
              {' · '}
              {o.receiptsSinceLastZ === 1
                ? '1 receipt since then'
                : `${o.receiptsSinceLastZ} receipts since then`}
              {!o.hasPrinter && (
                <span className={classes.warn}> &middot; no printer set for this shop</span>
              )}
            </Typography>

            <Grid container className={classes.actions}>
              <Button
                variant="outlined"
                color="primary"
                className={classes.xButton}
                disabled={busy}
                onClick={() => take(o, 'x')}
              >
                X reading
              </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={busy}
                onClick={() => setConfirmZ(o)}
              >
                Z reading &middot; close the shift
              </Button>
            </Grid>
          </CardContent>
        </Card>
      ))}

      {reading && (
        <Paper>
          <Toolbar>
            <Typography variant="h6">
              {reading.type} reading &middot; {reading.outlet}
            </Typography>
          </Toolbar>
          <div className={classes.slip}>{reading.text}</div>
        </Paper>
      )}

      <Dialog open={confirmZ !== null} onClose={() => setConfirmZ(null)}>
        <DialogTitle>Close the shift at {confirmZ?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This takes a Z reading and closes the period. The next reading
            starts from zero.
          </DialogContentText>
          <DialogContentText>
            <strong>It cannot be undone.</strong> If you only want to see the
            figures, take an X reading instead.
          </DialogContentText>
          <DialogContentText>
            {confirmZ?.receiptsSinceLastZ === 0
              ? 'There have been no receipts since the last Z, so this reading will be empty.'
              : `${confirmZ?.receiptsSinceLastZ} receipt(s) will be closed off.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmZ(null)}>Cancel</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => {
              const o = confirmZ;
              setConfirmZ(null);
              if (o) {
                take(o, 'z');
              }
            }}
          >
            Take the Z reading
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};
